import { findOverlayLayer } from './overlay-host.js'

export type ToastKind = 'info' | 'success' | 'warning' | 'error'
export interface ToastOptions {
  kind?: ToastKind
  /** null keeps a notification visible until dismissed. Active toasts never expire. */
  durationMs?: number | null
  active?: boolean
  /** Fraction from 0 to 1, or null for indeterminate progress. */
  progress?: number | null
  /**
   * Groups toasts about the same subject (e.g. `uploads:<folderId>`). A view that shows
   * this progress itself can hide matching toasts with `suppress(topic)`. Pass several
   * topics when more than one view covers the work (e.g. the folder and its ancestors).
   */
  topic?: string | readonly string[]
}

/** Progress reported by a source a toast tracks (see `Toasts.track`). */
export interface ToastProgressSnapshot {
  /** Fraction from 0 to 1, or null for indeterminate progress. */
  progress?: number | null
  message?: string
  /** The work finished successfully; the toast completes and vanishes after a while. */
  done?: boolean
  /** The work failed; the toast completes as an error and stays until dismissed. */
  error?: string
}

export interface ToastProgressSource {
  /** Calls the listener with each new snapshot; returns an unsubscribe function. */
  subscribe(listener: (snapshot: ToastProgressSnapshot) => void): () => void
}

export interface ToastTrackOptions extends Omit<ToastOptions, 'active' | 'progress' | 'durationMs'> {
  /** Message shown once the source reports `done` (defaults to the last message). */
  doneMessage?: string
  /** How long the completed toast stays visible (default 5s). */
  doneDurationMs?: number
}
export interface ToastUpdate { message?: string; kind?: ToastKind; progress?: number | null }
export interface ToastCompletion { message?: string; kind?: ToastKind; durationMs?: number | null }
export interface Toast {
  readonly id: string
  readonly message: string
  readonly kind: ToastKind
  readonly createdAt: number
  readonly expiresAt: number | null
  readonly active: boolean
  readonly visible: boolean
  readonly progress?: number | null
  readonly topic?: string | readonly string[]
}

function topicsOf(toast: Pick<Toast, 'topic'>): readonly string[] {
  if (toast.topic === undefined) return []
  return typeof toast.topic === 'string' ? [toast.topic] : toast.topic
}
export type ToastRenderer = (items: readonly Toast[], actions: { dismiss(id: string): void }) => void

/** Shared notification state. Applications may replace the default browser renderer. */
export class Toasts {
  private entries: Toast[] = []
  private recent: Toast[] = []
  private ongoing = new Map<string, Toast>()
  private activeListeners = new Set<(items: readonly Toast[]) => void>()
  private historyListeners = new Set<(items: readonly Toast[]) => void>()
  private leaving = new Set<HTMLElement>()
  private onScroll = (event: Event) => {
    if (event.target instanceof Node && this.host?.contains(event.target)) return
    for (const item of this.entries.slice()) if (item.expiresAt !== null && Date.now() - item.createdAt >= 1000) this.dismiss(item.id)
  }
  private timers = new Map<string, ReturnType<typeof setTimeout>>()
  private listeners = new Set<(items: readonly Toast[]) => void>()
  private renderer: ToastRenderer | null = null
  private host: HTMLElement | null = null
  private cards = new Map<string, HTMLElement>()
  private sequence = 0
  private disposed = false
  private suppressedTopics = new Map<string, number>()
  /** Active toasts hidden by `suppress()`; only these are shown again on release. */
  private hiddenBySuppression = new Set<string>()
  private trackers = new Map<string, () => void>()

  get items(): readonly Toast[] { return Object.freeze(this.entries.slice()) }

  /** Unfinished operations, newest first, including hidden notifications. Not history-limited. */
  get active(): readonly Toast[] { return Object.freeze([...this.ongoing.values()].reverse()) }

  subscribeActive(listener: (items: readonly Toast[]) => void): () => void {
    this.assertUsable()
    this.activeListeners.add(listener)
    try { listener(this.active) } catch {}
    return () => { this.activeListeners.delete(listener) }
  }

  /** Newest first, bounded to the last 50 notifications in this session. */
  get history(): readonly Toast[] { return Object.freeze(this.recent.slice()) }

  subscribeHistory(listener: (items: readonly Toast[]) => void): () => void {
    if (this.disposed) throw new Error('Toasts service has been destroyed')
    this.historyListeners.add(listener)
    try { listener(this.history) } catch {}
    return () => { this.historyListeners.delete(listener) }
  }

  clearHistory(): void {
    this.recent = []
    this.notifyHistory()
  }

  private notifyHistory(): void {
    for (const listener of this.historyListeners) { try { listener(this.history) } catch {} }
  }

  show(message: string, { kind = 'info', durationMs = 15000, active = false, progress, topic }: ToastOptions = {}): string {
    this.assertUsable()
    this.validate(message, kind, progress)
    this.validateDuration(durationMs)
    if (typeof active !== 'boolean' || !isValidTopic(topic)) throw new Error('Invalid toast options')
    if (Array.isArray(topic)) topic = Object.freeze(topic.slice())
    const hidden = this.isSuppressed({ topic })
    if (!hidden) while (this.entries.length >= 5) this.dismiss(this.entries[0].id)
    const id = `toast-${++this.sequence}`
    const createdAt = Date.now()
    const toast: Toast = Object.freeze({ id, message, kind, createdAt, active, visible: !hidden, progress, topic,
      expiresAt: active || durationMs === null || hidden ? null : createdAt + durationMs })
    if (hidden) {
      if (active) this.hiddenBySuppression.add(id)
    } else {
      this.entries.push(toast)
    }
    if (active) this.ongoing.set(id, toast)
    this.recent.unshift(toast); this.recent.length = Math.min(this.recent.length, 50)
    this.notifyHistory()
    this.notifyActive()
    this.schedule(toast)
    this.notify()
    return id
  }

  /** Update in place without changing visibility, lifetime or identity. */
  update(id: string, patch: ToastUpdate): boolean {
    this.assertUsable()
    const current = this.find(id)
    if (!current) return false
    const message = patch.message === undefined ? current.message : patch.message
    const kind = patch.kind === undefined ? current.kind : patch.kind
    const progress = patch.progress === undefined ? current.progress : patch.progress
    this.validate(message, kind, progress)
    this.replace(Object.freeze({ ...current, message, kind, progress }))
    this.changed()
    return true
  }

  /**
   * Show a progress toast that follows `source`. It updates with each snapshot, completes
   * as a success on `done` (and then vanishes after `doneDurationMs`) or as an error on
   * `error`. Dismissing hides it but keeps tracking. Returns the toast id.
   */
  track(message: string, source: ToastProgressSource, { doneMessage, doneDurationMs = 5000, ...options }: ToastTrackOptions = {}): string {
    this.assertUsable()
    if (!source || typeof source.subscribe !== 'function') throw new Error('Invalid toast progress source')
    const id = this.show(message, { ...options, active: true, progress: null })
    let finished = false
    let unsubscribe: (() => void) | null = null
    const stop = () => {
      finished = true
      this.trackers.delete(id)
      if (unsubscribe) { const off = unsubscribe; unsubscribe = null; try { off() } catch {} }
    }
    this.trackers.set(id, stop)
    unsubscribe = source.subscribe(snapshot => {
      if (finished || !snapshot) return
      if (snapshot.error) {
        this.complete(id, { kind: 'error', message: snapshot.error })
        stop()
      } else if (snapshot.done) {
        this.complete(id, { kind: 'success', message: doneMessage ?? snapshot.message, durationMs: doneDurationMs })
        stop()
      } else {
        this.update(id, { message: snapshot.message, progress: snapshot.progress })
      }
    })
    if (finished && unsubscribe) stop()
    return id
  }

  /**
   * Hide toasts with this topic while the caller shows the same progress itself (e.g. an
   * upload strip on the folder being uploaded to). Active ones reappear on release unless
   * they completed in the meantime. Returns the release function; calls are counted.
   */
  suppress(topic: string): () => void {
    this.assertUsable()
    if (typeof topic !== 'string' || !topic) throw new Error('Invalid toast topic')
    const count = this.suppressedTopics.get(topic) || 0
    this.suppressedTopics.set(topic, count + 1)
    if (count === 0) {
      for (const item of this.entries.slice()) {
        if (!topicsOf(item).includes(topic)) continue
        if (item.active) this.hiddenBySuppression.add(item.id)
        this.dismiss(item.id)
      }
    }
    let released = false
    return () => {
      if (released || this.disposed) return
      released = true
      const remaining = (this.suppressedTopics.get(topic) || 1) - 1
      if (remaining > 0) { this.suppressedTopics.set(topic, remaining); return }
      this.suppressedTopics.delete(topic)
      for (const item of [...this.ongoing.values()]) {
        if (topicsOf(item).includes(topic) && !this.isSuppressed(item) && this.hiddenBySuppression.delete(item.id)) this.reopen(item.id)
      }
    }
  }

  /** Restore a hidden active toast. Never creates a duplicate or revives completed work. */
  reopen(id: string): boolean {
    this.assertUsable()
    const current = this.ongoing.get(id)
    if (!current) return false
    if (current.visible) return true
    if (this.isSuppressed(current)) {
      this.hiddenBySuppression.add(id)
      return false
    }
    while (this.entries.length >= 5) this.dismiss(this.entries[0].id)
    const toast = Object.freeze({ ...current, visible: true })
    this.replace(toast)
    this.entries.push(toast)
    this.changed()
    return true
  }

  /** Finish once. Visible successes expire after 5s; errors/warnings stay until dismissed. */
  complete(id: string, { message, kind = 'success', durationMs = kind === 'success' || kind === 'info' ? 5000 : null }: ToastCompletion = {}): boolean {
    this.assertUsable()
    const current = this.ongoing.get(id)
    if (!current) return false
    const nextMessage = message === undefined ? current.message : message
    this.validate(nextMessage, kind)
    this.validateDuration(durationMs)
    const toast = Object.freeze({ ...current, message: nextMessage, kind, active: false,
      progress: kind === 'success' ? 1 : undefined,
      expiresAt: !current.visible || durationMs === null ? null : Date.now() + durationMs })
    this.ongoing.delete(id)
    this.hiddenBySuppression.delete(id)
    this.replace(toast)
    // A long-running operation may have aged out of recent history or survived clearHistory().
    this.recent = [toast, ...this.recent.filter(item => item.id !== id)].slice(0, 50)
    this.schedule(toast)
    this.changed()
    return true
  }

  private isSuppressed(toast: Pick<Toast, 'topic'>): boolean {
    return topicsOf(toast).some(topic => this.suppressedTopics.has(topic))
  }

  private assertUsable(): void {
    if (this.disposed) throw new Error('Toasts service has been destroyed')
  }

  private validate(message: string, kind: ToastKind, progress?: number | null): void {
    if (typeof message !== 'string' || !message.trim() || message.length > 2000) throw new Error('Toast requires a short message')
    if (!['info', 'success', 'warning', 'error'].includes(kind) ||
      (progress !== undefined && progress !== null && (!Number.isFinite(progress) || progress < 0 || progress > 1))) throw new Error('Invalid toast options')
  }

  private validateDuration(durationMs: number | null): void {
    if (durationMs !== null && (!Number.isFinite(durationMs) || durationMs < 1 || durationMs > 30000)) throw new Error('Invalid toast options')
  }

  private find(id: string): Toast | undefined {
    return this.ongoing.get(id) || this.entries.find(item => item.id === id) || this.recent.find(item => item.id === id)
  }

  private replace(toast: Toast): void {
    this.entries = this.entries.map(item => item.id === toast.id ? toast : item)
    this.recent = this.recent.map(item => item.id === toast.id ? toast : item)
    if (this.ongoing.has(toast.id)) this.ongoing.set(toast.id, toast)
  }

  private schedule(toast: Toast): void {
    clearTimeout(this.timers.get(toast.id)); this.timers.delete(toast.id)
    if (toast.visible && toast.expiresAt !== null) {
      this.timers.set(toast.id, setTimeout(() => this.dismiss(toast.id), Math.max(0, toast.expiresAt - Date.now())))
    }
  }

  private notifyActive(): void {
    for (const listener of this.activeListeners) { try { listener(this.active) } catch {} }
  }

  private changed(): void { this.notifyHistory(); this.notifyActive(); this.notify() }

  dismiss(id: string): void {
    clearTimeout(this.timers.get(id)); this.timers.delete(id)
    const index = this.entries.findIndex(entry => entry.id === id)
    if (index < 0) return
    this.replace(Object.freeze({ ...this.entries[index], visible: false, expiresAt: null }))
    this.entries.splice(index, 1); this.changed()
  }

  clear(): void {
    for (const stop of [...this.trackers.values()]) stop()
    this.hiddenBySuppression.clear()
    for (const timer of this.timers.values()) clearTimeout(timer)
    this.timers.clear(); this.entries = []; this.ongoing.clear(); this.clearHistory(); this.notifyActive(); this.notify()
  }

  subscribe(listener: (items: readonly Toast[]) => void): () => void {
    if (this.disposed) throw new Error('Toasts service has been destroyed')
    this.listeners.add(listener)
    try { listener(this.items) } catch {}
    return () => { this.listeners.delete(listener) }
  }

  /** Own rendering from an application/HUD layout; cleanup restores the default renderer. */
  useRenderer(renderer: ToastRenderer): () => void {
    if (this.disposed || typeof renderer !== 'function') throw new Error('Invalid toast renderer')
    if (this.renderer) { try { this.renderer([], { dismiss: id => this.dismiss(id) }) } catch {} }
    this.removeDefault()
    this.renderer = renderer
    this.notify()
    return () => {
      if (this.renderer !== renderer) return
      try { renderer([], { dismiss: id => this.dismiss(id) }) } catch {}
      this.renderer = null
      if (!this.disposed) this.notify()
    }
  }

  destroy(): void {
    if (this.disposed) return
    this.clear(); this.disposed = true; this.listeners.clear(); this.historyListeners.clear(); this.activeListeners.clear(); this.renderer = null; this.removeDefault()
  }

  private notify(): void {
    const items = this.items
    for (const listener of this.listeners) { try { listener(items) } catch {} }
    if (this.renderer) {
      try { this.renderer(items, { dismiss: id => this.dismiss(id) }); this.removeDefault(); return } catch { /* Keep notifications usable if an application renderer fails. */ }
    }
    this.renderDefault(items)
  }

  private removeDefault(): void {
    if (typeof document !== 'undefined') document.removeEventListener('scroll', this.onScroll, true)
    for (const card of [...this.cards.values(), ...this.leaving]) for (const animation of card.getAnimations()) animation.cancel()
    this.host?.remove(); this.host = null; this.cards.clear(); this.leaving.clear()
  }

  private reducedMotion(): boolean { return window.matchMedia('(prefers-reduced-motion: reduce)').matches }

  private leave(card: HTMLElement): void {
    this.leaving.add(card)
    card.style.pointerEvents = 'none'
    card.setAttribute('aria-hidden', 'true')
    card.querySelector('button')?.setAttribute('disabled', '')
    for (const animation of card.getAnimations()) animation.cancel()
    const finish = () => {
      card.remove(); this.leaving.delete(card)
      if (!this.entries.length && !this.cards.size && !this.leaving.size) this.removeDefault()
    }
    if (this.reducedMotion()) { finish(); return }
    const animation = card.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 250, easing: 'ease-out', fill: 'forwards' })
    animation.finished.then(finish, () => {})
  }

  private updateCard(card: HTMLElement, item: Toast): void {
    card.className = `ae-toast ae-toast--${item.kind}${item.active ? ' ae-toast--active' : ''}`
    const icon = card.querySelector<HTMLElement>('.ae-toast__icon')!
    icon.textContent = item.kind === 'success' ? '✓' : item.kind === 'error' || item.kind === 'warning' ? '!' : 'i'
    icon.style.color = `var(--ae-toast-accent,${item.kind === 'success' ? '#347b59' : item.kind === 'error' ? '#b53b36' : '#5367a6'})`
    const message = card.querySelector<HTMLElement>('.ae-toast__message')!
    if (message.textContent !== item.message) message.textContent = item.message
    const progress = card.querySelector<HTMLProgressElement>('.ae-toast__progress')!
    progress.hidden = !item.active || item.progress === undefined
    progress.setAttribute('aria-label', item.message)
    if (typeof item.progress === 'number') progress.value = item.progress
    else progress.removeAttribute('value')
  }

  private renderDefault(items: readonly Toast[]): void {
    if (typeof document === 'undefined' || !document.body) return
    if (!items.length && !this.host) return
    if (!this.host) {
      this.host = document.createElement('div')
      this.host.className = 'ae-toasts rr-block'
      this.host.setAttribute('role', 'status'); this.host.setAttribute('aria-live', 'polite'); this.host.setAttribute('aria-relevant', 'additions text')
      this.host.style.cssText = 'position:fixed;inset:var(--ae-toast-top,auto) var(--ae-toast-right,24px) var(--ae-toast-bottom,max(24px,env(safe-area-inset-bottom))) auto;width:min(380px,calc(100vw - 32px));display:flex;flex-direction:column;gap:10px;z-index:1000;pointer-events:none;font:inherit'
      const parent = document.fullscreenElement || findOverlayLayer(document, 'toast') || document.querySelector('.ae-root > .ae-overlay-host') || document.body
      parent.appendChild(this.host)
      document.addEventListener('scroll', this.onScroll, { capture: true, passive: true })
    }
    const active = new Set(items.map(item => item.id))
    for (const [id, card] of this.cards) if (!active.has(id)) { this.cards.delete(id); this.leave(card) }
    for (const item of items) {
      const existing = this.cards.get(item.id)
      if (existing) { this.updateCard(existing, item); continue }
      const card = document.createElement('div')
      card.style.cssText = 'display:flex;align-items:center;gap:12px;padding:14px 16px;border:1px solid var(--ae-toast-border,#dce2e8);border-radius:var(--ae-toast-radius,12px);background:var(--ae-toast-background,var(--ae-ui-background,#fff));color:var(--ae-toast-color,var(--ae-text-color,#253042));box-shadow:var(--ae-toast-shadow,0 8px 28px #13203020);pointer-events:auto;font-size:14px;line-height:1.45'
      const icon = document.createElement('span'); icon.className = 'ae-toast__icon'; icon.setAttribute('aria-hidden', 'true')
      icon.style.cssText = 'font-weight:700;font-size:18px'
      const content = document.createElement('div'); content.style.cssText = 'flex:1;min-width:0;overflow-wrap:anywhere'
      const message = document.createElement('span'); message.className = 'ae-toast__message'
      const progress = document.createElement('progress'); progress.className = 'ae-toast__progress'; progress.max = 1
      progress.style.cssText = 'width:100%;height:6px;accent-color:var(--ae-toast-accent,#5367a6)'
      content.append(message, progress)
      const close = document.createElement('button'); close.type = 'button'; close.textContent = '×'; close.setAttribute('aria-label', 'Dismiss notification')
      close.style.cssText = 'flex:none;width:32px;height:32px;border:0;border-radius:6px;background:transparent;color:inherit;font:inherit;font-size:22px;cursor:pointer'
      close.addEventListener('click', () => this.dismiss(item.id))
      card.append(icon, content, close); this.updateCard(card, item); this.host.appendChild(card); this.cards.set(item.id, card)
      if (!this.reducedMotion()) card.animate([{ transform: 'translateY(32px)', opacity: 0 }, { transform: 'translateY(0)', opacity: 1 }], { duration: 300, easing: 'cubic-bezier(0.2,0.8,0.2,1)' })
    }
  }
}

function isValidTopic(topic: unknown): boolean {
  if (topic === undefined) return true
  if (typeof topic === 'string') return topic.length > 0
  return Array.isArray(topic) && topic.length > 0 && topic.every(item => typeof item === 'string' && item.length > 0)
}
